import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import './AccountDetailsScreen.scss';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import Uploady, { useUploady, InputRef } from '@rpldy/uploady';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Area } from 'react-easy-crop';
import { useForm, Controller } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';
import LoaderButton from '../../../../components/common/LoaderButton';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { CommonDropdownType } from '../../../../types';
import accountDetailsFormToParams from '../../../../utils/accountDetailsFormToParams';
import { getCroppedImg } from '../../../../utils/avatarSectionHelpers';
import useCountries from '../../../../utils/countries';
import { extractDataFromPlaceDetails } from '../../../../utils/google';
import PlacesAutocomplete from '../../../../components/PlacesAutocomplete';
import type { PlaceDetails } from '../../../../api/services/PlacesService';
import userInfoToAccountDetailsForm from '../../../../utils/userInfoToAccountDetailsForm';
import { US_STATES_DROPDOWN_SHORT } from '../../../../utils/usStates';
import {
  selectAccountState,
  user,
  clearAvatarDeletedState,
  getMeThunk,
  uploadAvatarThunk,
  deleteAvatarThunk,
} from '../../../account/api/accountSlice';
import SilentAuthProvider, { WithSilentAuthFN } from '../../../app/components/SilentAuth';
import { selectAuthState } from '../../../auth/api/authSlice';
import { updatePatientThunk } from '../../api/AccountSlice';
import { AccountDetailsSchema } from '../../schemas';
import { AccountDetailsFormData } from '../../types';
import AvatarSection from './AvatarSection';
import NewEmailVerification from './NewEmailVerification';
import NewPhoneVerification from './NewPhoneVerification';
import alert from '../../../../utils/alert';
import Select from '../../../../components/Select';
import { MINOR_AGE } from '../../../../config';
import getAge from '../../../../utils/getAge';
import isNullOrUndefined from '../../../../utils/isNullOrUndefined';
import { parseLocalDate } from '../../../../utils/general';

const AccountDetails: FunctionComponent = () => {
  const states: CommonDropdownType[] = US_STATES_DROPDOWN_SHORT;
  const countries: CommonDropdownType[] = useCountries();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const autocompleteRef = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [emailEditable, setEmailEditable] = useState(false);
  const [phoneEditable, setPhoneEditable] = useState(false);

  const { newAvatar, newAvatarCropped, newAvatarCroppedAreaPixels, avatarDeleted } =
    useAppSelector(selectAccountState);

  const uploady = useUploady();
  const avatarInput = uploady.getInternalFileInput() as InputRef;
  const avatarInputFileObj = avatarInput.current?.files?.[0];

  const userInfo = useAppSelector(user);
  const auth = useAppSelector(selectAuthState);

  const userAge = userInfo?.birthDate ? getAge(new Date(userInfo?.birthDate)) : undefined;
  const isMinor = !isNullOrUndefined(userAge) ? (userAge as number) < MINOR_AGE : false;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    setValue,
    watch,
  } = useForm<AccountDetailsFormData>({
    mode: 'onChange',
    resolver: yupResolver(AccountDetailsSchema),
    context: { isMinor },
    defaultValues: userInfoToAccountDetailsForm(userInfo, t),
  });

  useEffect(() => {
    dispatch(clearAvatarDeletedState());
  }, [dispatch]);

  const handlePlaceSelected = (place: PlaceDetails) => {
    const { city, state, zip, country, formattedAddress, streetNumber, route } =
      extractDataFromPlaceDetails(place);

    setValue('city', city?.long_name || '', { shouldValidate: true, shouldDirty: true });
    setValue('zip', zip?.long_name || '', { shouldValidate: true, shouldDirty: true });
    if (state) {
      setValue(
        'state',
        { label: state.long_name, value: state.long_name },
        { shouldValidate: true, shouldDirty: true },
      );
    }
    if (country) {
      setValue(
        'country',
        { label: country.long_name, value: country.long_name },
        { shouldValidate: true, shouldDirty: true },
      );
    }
    const streetValue = route
      ? `${streetNumber ? `${streetNumber.long_name} ` : ''}${route.long_name}`
      : formattedAddress?.split(',')[0] || '';
    setValue('street', streetValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (userInfo?.uuid) {
      dispatch(getMeThunk(userInfo.uuid));
    }
  }, [dispatch, userInfo?.uuid]);

  const onSubmit = async (data: AccountDetailsFormData) => {
    setIsLoading(true);
    try {
      const userData = accountDetailsFormToParams(
        {
          email: data.email,
          firstName: data.firstName,
          middleName: data.middleName,
          lastName: data.lastName,
          phone: data.phone,
          street: data.street,
          unit: data.unit,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: data.country,
          birthdate: moment(data.birthdate).locale('en').format('MMM D, YYYY'),
          sex: data.sex,
        },
        userInfo?.identifier,
      );

      const accessToken = auth.accessToken ?? '';
      const uuid = userInfo?.uuid ?? '';

      const params = {
        userData,
        accessToken,
        uuid,
        callback: async (success: boolean) => {
          if (success) {
            await dispatch(getMeThunk()).unwrap();
            alert.success(t('Account Details saved successfully!'), {
              toastId: 'update-success',
            });
          } else {
            alert.error(t('Updating account details failed. Please try again.'), {
              toastId: 'update-error',
            });
          }
        },
      };

      await dispatch(updatePatientThunk(params)).unwrap();

      if (newAvatar && avatarInputFileObj) {
        if (avatarInputFileObj && newAvatar && newAvatarCropped) {
          const croppedAvatarImage = await getCroppedImg(
            URL.createObjectURL(avatarInputFileObj as any),
            newAvatarCroppedAreaPixels as Area,
          );
          await dispatch(
            uploadAvatarThunk({
              file: croppedAvatarImage as Blob,
              fileName: avatarInputFileObj?.name || '',
              fileTag: 'avatar',
            }),
          ).unwrap();
        }
      }
      if (avatarDeleted) {
        await dispatch(deleteAvatarThunk()).unwrap();
        await dispatch(getMeThunk());
      }
    } catch (err) {
      alert.error(t('Updating account details failed. Please try again.'), {
        toastId: 'update-error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailEdit = useCallback((withSilentAuth: WithSilentAuthFN) => {
    withSilentAuth(() => {
      setEmailEditable(true);
    });
  }, []);

  const handlePhoneEdit = useCallback((withSilentAuth: WithSilentAuthFN) => {
    withSilentAuth(() => {
      setPhoneEditable(true);
    });
  }, []);

  const getUserContactPoint = useMemo(
    () =>
      (contactType: string): string => {
        return (
          userInfo?.telecom?.find((t: fhir4.ContactPoint) => t.system === contactType)?.value ?? ''
        );
      },
    [userInfo?.telecom],
  );

  return (
    <SilentAuthProvider
      renderItem={(withSilentAuth) => (
        <>
          <div style={{ maxWidth: 1024 }}>
            <div className='row g-4 mb-4'>
              {/* AVATAR */}
              <AvatarSection />
            </div>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <div className='row g-4 mb-4'>
                {/* EMAIL */}
                <div className='col-md-6 col-lg-4'>
                  <Form.Group className='form-group'>
                    <div className='d-flex justify-content-between flex-row'>
                      <Form.Label>{t('Email')}</Form.Label>
                      <Form.Label>
                        <button
                          className='p-0 h-100 page__subsection-link text-primary-700'
                          onClick={() => {
                            handleEmailEdit(withSilentAuth);
                          }}
                          type='button'
                        >
                          {t('Edit')}
                        </button>
                      </Form.Label>
                    </div>
                    <p className='mt-3 text-break'>{getUserContactPoint('email')}</p>
                  </Form.Group>
                </div>

                {/* PHONE */}
                <div className='col-md-6 col-lg-4'>
                  <Form.Group className='form-group'>
                    <div className='d-flex justify-content-between flex-row'>
                      <Form.Label>{t('Phone')}</Form.Label>
                      <Form.Label>
                        <button
                          className='p-0 h-100 page__subsection-link text-primary-700'
                          onClick={() => {
                            handlePhoneEdit(withSilentAuth);
                          }}
                          type='button'
                        >
                          {t('Edit')}
                        </button>
                      </Form.Label>
                    </div>
                    <p className='mt-3'>{getUserContactPoint('phone')}</p>
                  </Form.Group>
                </div>
              </div>
              <div className='row g-4 mb-4'>
                <Form.Group className='col-md-6 col-lg-4'>
                  <Form.Label>{t('First Name')}</Form.Label>
                  <Form.Control type='text' {...register('firstName')} />
                  {errors.firstName?.message && (
                    <Form.Text className='text-danger'>{t(errors.firstName.message)}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className='col-md-6 col-lg-4'>
                  <Form.Label>{t('Middle Name')}</Form.Label>
                  <Form.Control type='text' {...register('middleName')} />
                  {errors.middleName?.message && (
                    <Form.Text className='text-danger'>{t(errors.middleName.message)}</Form.Text>
                  )}
                </Form.Group>
                <Form.Group className='col-md-6 col-lg-4'>
                  <Form.Label>{t('Last Name')}</Form.Label>
                  <Form.Control type='text' {...register('lastName')} />
                  {errors.lastName?.message && (
                    <Form.Text className='text-danger'>{t(errors.lastName.message)}</Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className='row g-4 mb-4'>
                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Date of Birth')}</Form.Label>
                  <Controller
                    control={control}
                    name='birthdate'
                    render={({ field }) => {
                      // Handle both Date objects and date strings (YYYY-MM-DD)
                      let selectedDate: Date | null = null;
                      if (field.value) {
                        if (field.value instanceof Date) {
                          selectedDate = field.value;
                        } else if (typeof field.value === 'string') {
                          // If it's a string in YYYY-MM-DD format, parse it as local date
                          if (/^\d{4}-\d{2}-\d{2}$/.test(field.value)) {
                            selectedDate = parseLocalDate(field.value);
                          } else {
                            selectedDate = new Date(field.value);
                          }
                        }
                      }
                      return (
                        <div className='datepicker-wrapper'>
                          <DatePicker
                            disabled={isMinor}
                            onChange={(date: Date | null) => field.onChange(date)}
                            selected={selectedDate}
                            showYearDropdown
                            scrollableYearDropdown
                            yearDropdownItemNumber={100}
                            showMonthDropdown
                            dropdownMode='scroll'
                            maxDate={new Date()}
                            dateFormat='MM/dd/yyyy'
                            className='form-control'
                            placeholderText='Select date of birth'
                            calendarClassName='custom-datepicker-calendar'
                          />
                        </div>
                      );
                    }}
                  />
                  {errors.birthdate?.message && (
                    <Form.Text className='text-danger'>{t(errors.birthdate.message)}</Form.Text>
                  )}
                </Form.Group>
                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Sex')}</Form.Label>
                  <Controller
                    name='sex'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={[
                          { value: 'male', label: t('Male') },
                          { value: 'female', label: t('Female') },
                          { value: 'other', label: t('Other') },
                        ]}
                        placeholder={t('Select')}
                        menuPlacement='top'
                      />
                    )}
                  />
                  {errors.sex?.message && (
                    <Form.Text className='text-danger'>{t(errors.sex.message)}</Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className='row g-4 mb-4'>
                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Street')}</Form.Label>
                  <PlacesAutocomplete
                    autocompleteRef={autocompleteRef}
                    onPlaceSelected={handlePlaceSelected}
                    placeholder={t('Street')}
                    value={watch('street')}
                    onChange={(value) =>
                      setValue('street', value, { shouldValidate: true, shouldDirty: true })
                    }
                  />
                  {errors.street?.message && (
                    <Form.Text className='text-danger'>{t(errors.street.message)}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className='col-md-6'>
                  <Form.Label>{t('City')}</Form.Label>
                  <Form.Control type='text' {...register('city')} />
                  {errors.city?.message && (
                    <Form.Text className='text-danger'>{t(errors.city.message)}</Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className='row g-4 mb-4'>
                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Unit')}</Form.Label>
                  <Form.Control type='text' {...register('unit')} />
                  {errors.unit?.message && (
                    <Form.Text className='text-danger'>{t(errors.unit.message)}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Zip')}</Form.Label>
                  <Form.Control type='text' {...register('zip')} />
                  {errors.zip?.message && (
                    <Form.Text className='text-danger'>{t(errors.zip.message)}</Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className='row g-4 mb-4'>
                <Form.Group className='col-md-6'>
                  <Form.Label>{t('State')}</Form.Label>
                  <Controller
                    name='state'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={states}
                        placeholder={t('Select')}
                        menuPlacement='top'
                      />
                    )}
                  />
                  {errors.state?.message && (
                    <Form.Text className='text-danger'>{t(errors.state.message)}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className='col-md-6'>
                  <Form.Label>{t('Country')}</Form.Label>
                  <Controller
                    name='country'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={countries}
                        placeholder={t('Select')}
                        menuPlacement='top'
                      />
                    )}
                  />
                  {errors.country?.message && (
                    <Form.Text className='text-danger'>{t(errors.country.message)}</Form.Text>
                  )}
                </Form.Group>
              </div>

              <LoaderButton
                className='btn btn-primary auth__btn-primary w-100 mb-3'
                type='submit'
                isSubmitting={isLoading}
                disabled={(!isDirty && !newAvatar && !avatarDeleted) || isLoading}
              >
                {t('Save Changes')}
              </LoaderButton>
            </Form>
          </div>

          <NewPhoneVerification phoneEditable={phoneEditable} setPhoneEditable={setPhoneEditable} />
          <NewEmailVerification emailEditable={emailEditable} setEmailEditable={setEmailEditable} />
        </>
      )}
    />
  );
};

export const AccountDetailsScreen = () => {
  return (
    <Uploady
      accept='image/jpeg,image/png,image/jpg'
      inputFieldName='account-logo-field'
      fileInputId='account-logo-field-input'
    >
      <AccountDetails />
    </Uploady>
  );
};
